/**
 * -----------------------------------------------------------------------------
 * Popup - Create New User
 * -----------------------------------------------------------------------------
 * This popup is used for creating a new user in the organization.
 */

import { Formik, useFormik } from "formik";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useCreateOrganizationUserMutation, useOrganizationRolesPermissionsQuery } from "../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { TModalsState, setModalsData, TListingQueryParams } from "../../../api/store/commonSlice";
import { TLegacyUserRole } from "../../../api/types/UserRole";
import { AdminFormFieldDropdown, AdminFormFieldInput, AdminFormFieldSubmit } from "../../../components/admin/formFields";
import { routeUrls } from "../../../navigation/routeUrls";
import { userRoleDisplayName } from "../../../utils/user";
import { formInitialValues, TFormFieldNames, YupValidationSchema } from "./validation";
import FTModal from "../../../components/common/FTModal";
import FormActionButtons from "../../../components/admin/formActionButtons";

const AdminsUsersCreateNew = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation('translation', { keyPrefix: 'admins.users.create_new' });
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);
  const listingQueryParams: TListingQueryParams = useSelector((state: any) => state.commonReducer.listingQueryParams);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // fetching roles and permissions
  const {
    data: dataOrgRolesPermissions,
    isFetching: isFetchingOrgRolesPermissions,
  } = useOrganizationRolesPermissionsQuery(
    `${listingQueryParams.users.accountid}`,
    { skip: !listingQueryParams.users.accountid }
  );

  // user mutation
  const [createOrgUserAPITrigger] = useCreateOrganizationUserMutation();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showCreateUser: false }));
  };
  
    const formik = useFormik({
        initialValues:formInitialValues,
        validationSchema:YupValidationSchema,
        onSubmit:(values, { setSubmitting }) => {
          // if(APP_CONFIG.TOASTS.INFO) toast.info(t('loginScreen.toast.logging_you_in'));
          createOrgUserAPITrigger({
            account_id: `${listingQueryParams.users.accountid}`,
            user_id: values.username,
            email: values.email,
            role_id: values.acl_role,
          })
            .unwrap()
            .then((data) => {
              if (!!data?.user_id) {
                dispatch(setModalsData({ ...modalsState, showCreateUser: false }));
                toast.success(t('toast.create_success'), { autoClose: 10000 });
                navigate(`${routeUrls.dashboardChildren.adminChildren.users}/${data?.user_id}`, { state: { new: true } });
              } else {
                toast.error(t('toast.create_failed'));
              }
            })
            .catch((error) => {
              const errors = !!error?.data ? serializeErrorKeyValues(error?.data) : [t('toast.create_failed')];
              toast.error(errors?.join(' '));
            })
            .finally(() => {
              setSubmitting(false);
            });
        }
      });
      const{
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        dirty,
        setFieldValue,
      } = formik;
    const invalidFields =
      Object.keys(errors)
        .filter(key => !!errors[key as TFormFieldNames]);
    const isFormValid = invalidFields.length === 0 && dirty;
    const MemoizedFormActionButtons = useMemo(() => {
      return (
        <>
          <FormActionButtons
            buttons={[
              {
                label:t('submit_button'),
                variant:"primary",
                disabled:!isFormValid || isSubmitting || isFetchingOrgRolesPermissions /* || isLoading*/
              },
              {
                label:tMain('cancel'),
                type:"button",
                variant:"danger-transparent",
                disabled:isSubmitting || isFetchingOrgRolesPermissions ,/* || isLoading*/
                onClick: () => hideModal()
              },
            ]}
          />
        </>
      );
    }, [
      isFormValid,
      isSubmitting, 
      isFetchingOrgRolesPermissions       
    ]); 
  return (
    <FTModal
      visible={modalsState.showCreateUser}
      heading={t("heading")}
      onHide={hideModal}
      disableOverlay={true}
    >
          <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
            <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
              {t("sub_heading")}
            </p>

            <AdminFormFieldInput
              label={t('user_id')}
              type="text"
              id="UserName"
              name="username"
              placeholder={t('user_name_placeholder')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.username}
              touched={touched.username}
              error={errors.username} />

            <AdminFormFieldInput
              label={t('email_address')}
              type="email"
              id="Email"
              name="email"
              placeholder={t('email_address_placeholder')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.email}
              touched={touched.email}
              error={errors.email} />

            <AdminFormFieldDropdown
              label={t('acl_role')}
              id="ALCRole"
              name="alc_role"
              placeholder={t('acl_role_placeholder')}
              onChange={e => {
                setFieldValue('acl_role', `${e?.value}`)
              }}
              onBlur={handleBlur}
              value={values.acl_role}
              options={dataOrgRolesPermissions?.map((item: TLegacyUserRole, i: number) => {
                return { value: `${item.roleid}`, label: userRoleDisplayName(item) }
              }) || []}
              touched={touched.acl_role}
              error={errors.acl_role} />
            {MemoizedFormActionButtons}
            {/* <AdminFormFieldSubmit
              label={t('submit_button')}
              variant="primary"
              disabled={!isFormValid || isSubmitting || isFetchingOrgRolesPermissions} // || isLoading
            />

            <AdminFormFieldSubmit
              label={tMain('cancel')}
              type="button"
              variant="danger-transparent"
              disabled={isSubmitting || isFetchingOrgRolesPermissions} // || isLoading
              onClick={hideModal}
            /> */}

            <p className="col-span-6 mt-0 text-center text-sm leading-relaxed text-field-info-text">
              {t("bottom_text_1")}{" "}
              <Link to={routeUrls.dashboardChildren.adminChildren.roles} className="text-accent-blue-bright font-bold hover:underline">{t("bottom_text_click_here")}</Link>{" "}
              {t("bottom_text_2")}
            </p>
          </form>
    </FTModal>
  )
};
export default AdminsUsersCreateNew;